import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useCriticalityData,
  useDetails,
  useUpdateCriticalityAssessment,
} from "../../hooks";

interface CriticalityAssessmentStepProps {
  editMode: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  networkId?: string;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const CriticalityAssessmentStep: React.FC<CriticalityAssessmentStepProps> = ({
  editMode,
  onNext,
  networkId,
  onPrevious,
  isFirstStep,
  setValidationError,
  clearValidationError,
}) => {
  const { id, action } = useParams();
  const remoteUtilityId = getRemoteUtilityId();
  const dataLoadedRef = useRef(false);
  useEffect(() => {}, [networkId]);
  const [criticalityLevel, setCriticalityLevel] = useState<
    "Low" | "Medium" | "High" | "Critical"
  >("Low");
  const [criticalityData, setCriticalityData] = useState<{
    [key: string]: number;
  }>({});

  // API hooks - Force refetch every time component mounts
  const { data: networkData, refetch: refetchNetworkData } = useDetails({
    networkId: id || "",
  });
  const [criticalityScore, setCriticalityScore] = useState(
    Math.round(networkData?.result.criticalityScore) || 0
  );

  const { data: criticalityApiData } = useCriticalityData({
    remote_utility_id: remoteUtilityId,
    show_inactive: "false",
  });
  const updateCriticalityMutation = useUpdateCriticalityAssessment();

  // Force refetch network data when component mounts
  useEffect(() => {
    if (id) {
      refetchNetworkData();
      dataLoadedRef.current = false; // Reset data loaded flag
    }
  }, [id, refetchNetworkData]);

  // Initialize dynamic criticality fields with default value of 3
  const initializeDynamicFields = useMemo(() => {
    if (
      !criticalityApiData?.result ||
      !Array.isArray(criticalityApiData.result)
    )
      return {};

    const dynamicFields: any = {};
    criticalityApiData.result
      .filter((item) => item.isActive === true)
      .forEach((item) => {
        dynamicFields[item.code] = 3; // Default to 3
      });

    return dynamicFields;
  }, [criticalityApiData]);

  // Create form instance
  const form = FormService.createForm({
    fields: [],
    defaultValues: criticalityData,
  });

  // ONLY autopopulate when action is "edit" and networkData is available
  useEffect(() => {
    if (
      (action === "edit" || id) &&
      networkData?.result &&
      !dataLoadedRef.current
    ) {
      // Extract criticality scores from networkData.result.criticality_assessment
      const criticalityScores: any = {};
      if (
        networkData.result.criticalityAssessment &&
        Array.isArray(networkData.result.criticalityAssessment)
      ) {
        networkData.result.criticalityAssessment.forEach((item: any) => {
          if (item.code && item.score !== undefined) {
            criticalityScores[item.code] = item.score;
          }
        });
      }

      // For dynamic criticality fields, use scores from criticality_assessment or default to 3
      const finalCriticalityData = Object.keys(initializeDynamicFields).reduce(
        (acc, key) => {
          acc[key] =
            criticalityScores[key] !== undefined ? criticalityScores[key] : 3;
          return acc;
        },
        {} as any
      );

      setCriticalityData(finalCriticalityData);

      // Set form values directly
      try {
        Object.keys(finalCriticalityData).forEach((key) => {
          form.setValue(key, finalCriticalityData[key]);
        });
      } catch (error) {}

      dataLoadedRef.current = true;
    }
  }, [action, networkData, initializeDynamicFields, id]);

  // Initialize criticality data with default score of 3 (for create mode)
  useEffect(() => {
    if (
      criticalityApiData?.result &&
      Object.keys(criticalityData).length === 0 &&
      action !== "edit" &&
      !id
    ) {
      const initialData: { [key: string]: number } = {};
      criticalityApiData.result.forEach((item: any) => {
        if (item.isActive) {
          // Set default score to 3 instead of using item.score
          initialData[item.code] = 3;
        }
      });
      setCriticalityData(initialData);
    }
  }, [criticalityApiData, criticalityData, action, id]);

  // Calculate criticality score using the correct formula
  useEffect(() => {
    if (criticalityApiData?.result && Object.keys(criticalityData).length > 0) {
      // Correct formula: ((weight/100) * score) add all Assessment Parameters then multiply *20
      const score = criticalityApiData.result.reduce(
        (total: number, item: any) => {
          if (item.isActive && criticalityData[item.code] !== undefined) {
            // Apply the correct formula: (weight/100) * score
            const parameterScore =
              (item.weightage / 100) * criticalityData[item.code];
            return total + parameterScore;
          }
          return total;
        },
        0
      );

      // Multiply the sum by 20 as per the formula
      const finalScore = score * 20;

      setCriticalityScore(Math.round(finalScore));

      // Determine criticality level based on the score
      let level: "Low" | "Medium" | "High" | "Critical";
      if (finalScore <= 25) level = "Low";
      else if (finalScore <= 50) level = "Medium";
      else if (finalScore <= 75) level = "High";
      else level = "Critical";

      setCriticalityLevel(level);
    }
  }, [criticalityData, criticalityApiData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setCriticalityData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleNext = async () => {
    try {
      // Prepare the payload for the API
      const criticalityAssessment =
        criticalityApiData?.result
          ?.filter((item: any) => item.isActive)
          .map((item: any) => ({
            name: item.name,
            score: criticalityData[item.code] || 3, // Use 3 as fallback instead of item.score
            code: item.code,
            weight: item.weightage,
          })) || [];

      const payload = {
        remote_utility_id: remoteUtilityId,
        criticality_assessment: criticalityAssessment,
      };

      await updateCriticalityMutation.mutateAsync({
        networkId: networkId || id,
        payload,
      });
      await refetchNetworkData();
      clearValidationError?.();
      onNext?.();
    } catch (error) {
      setValidationError?.(
        "Failed to update criticality assessment. Please try again."
      );
    }
  };

  const getCriticalityColor = (level: string) => {
    switch (level) {
      case "Low":
        return "bg-blue-100 text-blue-800";
      case "Medium":
        return "bg-yellow-100 text-yellow-800";
      case "High":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Generate form fields from API data with default value of 3
  const formFields: FormField[] =
    criticalityApiData?.result
      ?.filter((item: any) => item.isActive)
      .map((item: any) => ({
        name: item.code,
        label: item.name,
        type: "slider",
        min: 1,
        max: 5,
        step: 1,
        showHelperTooltip: true,
        tooltipText: item.description || `Assessment for ${item.name}`,
        defaultValue: criticalityData[item.code] || 3, // Use 3 as default instead of item.score
        suffix: `(${item.weightage.toFixed(1)}% weight)`,
        helperText:
          item.description || `Rate from 1 (Very Low) to 5 (Very High).`,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-3",
          slider:
            "w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-2",
        },
      })) || [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="page-title">Criticality Assessment</h2>
        <p className="page-subtitle">
          Auto-scored based on network parameters. Adjust individual scores if
          needed.
        </p>
      </div>

      <div className="space-y-8">
        {/* Criticality Sliders */}
        <div>
          <h3 className="text-lg font-medium mb-4">Assessment Parameters</h3>
          <p className="text-sm text-gray-600 mb-6">
            Adjust the sliders below to reflect the specific characteristics of
            this network.
          </p>

          <DynamicForm
            fields={formFields}
            form={form}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              layout: "vertical",
              spacing: "comfortable",
            }}
          />
        </div>

        {/* Overall Score Display */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <h3 className="font-semibold">Overall Criticality Score</h3>
            <p className="text-2xl font-bold">{criticalityScore}/100</p>
          </div>
          <Badge className={getCriticalityColor(criticalityLevel)}>
            {criticalityLevel}
          </Badge>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={onPrevious} disabled={isFirstStep}>
          Previous
        </Button>
        <Button
          onClick={handleNext}
          isLoading={updateCriticalityMutation.isPending}
        >
          {editMode ? "Update Network" : "Next"}
        </Button>
      </div>
    </div>
  );
};

export default CriticalityAssessmentStep;
